import axiosInstance from '../../../config/axios';

export type ProjectStatus =
  | 'LEAD'
  | 'OFFER_CREATED'
  | 'OFFER_SENT'
  | 'ORDER_CONFIRMED'
  | 'IN_EXECUTION'
  | 'COMPLETED'
  | 'WARRANTY';

export type ProjectType = 'BAD' | 'HEIZUNG' | 'NEUBAU' | 'WARTUNG' | 'LUEFTUNG' | 'KLIMA' | 'SONSTIGE';
export type ProjectPriority = 'NORMAL' | 'URGENT' | 'EMERGENCY';
export type ProjectSource = 'REFERRAL' | 'WEBSITE' | 'RETURNING' | 'ARCHITECT' | 'OTHER';

export interface ExternalPartnerDisplay {
  id: number;
  name: string;
}

export interface Project {
  id: number;
  partner: number;
  partner_name?: string;
  partner_kind?: string;
  name: string;
  reference?: string;
  status: ProjectStatus;
  description?: string;
  street?: string;
  house_no?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  notes?: string;
  start_date?: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
  project_type?: ProjectType;
  project_type_display?: string;
  priority?: ProjectPriority;
  priority_display?: string;
  responsible_person?: number;
  responsible_person_name?: string;
  external_partners?: number[];
  external_partners_display?: ExternalPartnerDisplay[];
  source?: ProjectSource;
  source_display?: string;
  planned_start?: string;
  planned_end?: string;
}

export interface CreateProjectPayload {
  partner: number;
  name: string;
  reference?: string;
  status: ProjectStatus;
  street?: string;
  house_no?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  notes?: string;
  project_type?: ProjectType;
  priority?: ProjectPriority;
  responsible_person?: number;
  external_partners?: number[];
  source?: ProjectSource;
  planned_start?: string;
  planned_end?: string;
}

export interface UpdateProjectPayload extends Partial<Omit<CreateProjectPayload, 'partner'>> {}

export interface ProjectListParams {
  partner_id?: number;
  status?: ProjectStatus;
  search?: string;
}

const BASE = '/api/admin/projects/';

function unwrap<T>(data: any): T[] {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

export default class ProjectService {
  static async list(params?: ProjectListParams): Promise<Project[]> {
    const cleanParams = params
      ? Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined && v !== ''))
      : {};
    const { data } = await axiosInstance.get(BASE, { params: cleanParams });
    return unwrap<Project>(data);
  }

  static async listByPartner(partnerId: number): Promise<Project[]> {
    return this.list({ partner_id: partnerId });
  }

  static async searchByPartner(partnerId: number, searchTerm?: string): Promise<Project[]> {
    return this.list({ partner_id: partnerId, search: searchTerm });
  }

  static async get(id: number): Promise<Project> {
    const { data } = await axiosInstance.get(`${BASE}${id}/`);
    return data as Project;
  }

  static async create(payload: CreateProjectPayload): Promise<Project> {
    const { data } = await axiosInstance.post(BASE, payload);
    return data as Project;
  }

  static async update(id: number, payload: UpdateProjectPayload): Promise<Project> {
    const cleanPayload = Object.fromEntries(
      Object.entries(payload).filter(([_, v]) => v !== undefined)
    );
    const { data } = await axiosInstance.patch(`${BASE}${id}/`, cleanPayload);
    return data as Project;
  }

  static async delete(id: number): Promise<void> {
    await axiosInstance.delete(`${BASE}${id}/`);
  }

  static getStatusLabel(status: ProjectStatus): string {
    switch (status) {
      case 'LEAD':
        return 'Lead';
      case 'OFFER_CREATED':
        return 'Angebot erstellt';
      case 'OFFER_SENT':
        return 'Angebot versendet';
      case 'ORDER_CONFIRMED':
        return 'Auftrag bestätigt';
      case 'IN_EXECUTION':
        return 'In Ausführung';
      case 'COMPLETED':
        return 'Abgeschlossen';
      case 'WARRANTY':
        return 'Gewährleistung';
      default:
        return status;
    }
  }

  static getStatusColor(status: ProjectStatus): { bg: string; text: string } {
    switch (status) {
      case 'LEAD':
        return { bg: '#dbeafe', text: '#1e40af' };
      case 'OFFER_CREATED':
        return { bg: '#fef3c7', text: '#92400e' };
      case 'OFFER_SENT':
        return { bg: '#fde68a', text: '#78350f' };
      case 'ORDER_CONFIRMED':
        return { bg: '#bbf7d0', text: '#166534' };
      case 'IN_EXECUTION':
        return { bg: '#d1fae5', text: '#065f46' };
      case 'COMPLETED':
        return { bg: '#e0e7ff', text: '#3730a3' };
      case 'WARRANTY':
        return { bg: '#f3e8ff', text: '#7c3aed' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  }

  static getPriorityLabel(priority: ProjectPriority): string {
    switch (priority) {
      case 'NORMAL':
        return 'Normal';
      case 'URGENT':
        return 'Eilig';
      case 'EMERGENCY':
        return 'Notfall';
      default:
        return priority;
    }
  }

  static getPriorityColor(priority: ProjectPriority): { bg: string; text: string } {
    switch (priority) {
      case 'NORMAL':
        return { bg: '#f3f4f6', text: '#374151' };
      case 'URGENT':
        return { bg: '#fef3c7', text: '#92400e' };
      case 'EMERGENCY':
        return { bg: '#fee2e2', text: '#991b1b' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  }

  static getProjectTypeLabel(type: ProjectType): string {
    switch (type) {
      case 'BAD':
        return 'Badmodernisierung';
      case 'HEIZUNG':
        return 'Heizung';
      case 'NEUBAU':
        return 'Neubau';
      case 'WARTUNG':
        return 'Wartung';
      case 'LUEFTUNG':
        return 'Lüftung';
      case 'KLIMA':
        return 'Klima';
      case 'SONSTIGE':
        return 'Sonstige';
      default:
        return type;
    }
  }

  static getSourceLabel(source: ProjectSource): string {
    switch (source) {
      case 'REFERRAL':
        return 'Empfehlung';
      case 'WEBSITE':
        return 'Website';
      case 'RETURNING':
        return 'Wiederkehrender Kunde';
      case 'ARCHITECT':
        return 'Architekt';
      case 'OTHER':
        return 'Sonstige';
      default:
        return source;
    }
  }
}

export const PROJECT_STATUS_OPTIONS: { value: ProjectStatus; label: string }[] = [
  { value: 'LEAD', label: 'Lead' },
  { value: 'OFFER_CREATED', label: 'Draft' },
  { value: 'OFFER_SENT', label: 'Sent' },
  { value: 'ORDER_CONFIRMED', label: 'Accepted' },
  { value: 'IN_EXECUTION', label: 'Active' },
  { value: 'COMPLETED', label: 'Done' },
  { value: 'WARRANTY', label: 'Warranty' },
];

export const PROJECT_TYPE_OPTIONS: { value: ProjectType; label: string }[] = [
  { value: 'BAD', label: 'Badmodernisierung' },
  { value: 'HEIZUNG', label: 'Heizung' },
  { value: 'NEUBAU', label: 'Neubau' },
  { value: 'WARTUNG', label: 'Wartung' },
  { value: 'LUEFTUNG', label: 'Lüftung' },
  { value: 'KLIMA', label: 'Klima' },
  { value: 'SONSTIGE', label: 'Sonstige' },
];

export const PROJECT_PRIORITY_OPTIONS: { value: ProjectPriority; label: string }[] = [
  { value: 'NORMAL', label: 'Normal' },
  { value: 'URGENT', label: 'Eilig' },
  { value: 'EMERGENCY', label: 'Notfall' },
];

export const PROJECT_SOURCE_OPTIONS: { value: ProjectSource; label: string }[] = [
  { value: 'REFERRAL', label: 'Empfehlung' },
  { value: 'WEBSITE', label: 'Website' },
  { value: 'RETURNING', label: 'Wiederkehrender Kunde' },
  { value: 'ARCHITECT', label: 'Architekt' },
  { value: 'OTHER', label: 'Sonstige' },
];
